import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Autocomplete,
  Paper
} from '@mui/material';
import {
  DEFAULT_TEXT_FALLBACK,
  DEFAULT_IMAGE_FALLBACK,
  AI_PROVIDERS,
  AI_IMAGE_PROVIDERS
} from '../constants/aiModels';

function FallbackChainSummary({ isImageAgent, config }) {
  if (isImageAgent) {
    const retries = config.image_max_retries ?? DEFAULT_IMAGE_FALLBACK.image_max_retries;
    const delayAfter = config.image_retry_delay_after ?? DEFAULT_IMAGE_FALLBACK.image_retry_delay_after;
    const delaySec = config.image_retry_delay_seconds ?? DEFAULT_IMAGE_FALLBACK.image_retry_delay_seconds;
    const fbProvider = config.image_fallback_provider ?? DEFAULT_IMAGE_FALLBACK.image_fallback_provider;
    const fbModel = config.image_fallback_model ?? DEFAULT_IMAGE_FALLBACK.image_fallback_model;

    return (
      <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5', mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          {`Modelo primario (${retries} intentos, espera ${delaySec}s despues del #${delayAfter})\n  -> ${fbProvider}/${fbModel} (fallback final)`}
        </Typography>
      </Paper>
    );
  }

  const retries = config.max_retries ?? DEFAULT_TEXT_FALLBACK.max_retries;
  const fb1Provider = config.primary_fallback_provider ?? DEFAULT_TEXT_FALLBACK.primary_fallback_provider;
  const fb1Model = config.primary_fallback_model ?? DEFAULT_TEXT_FALLBACK.primary_fallback_model;
  const fb2Provider = config.secondary_fallback_provider ?? DEFAULT_TEXT_FALLBACK.secondary_fallback_provider;
  const fb2Model = config.secondary_fallback_model ?? DEFAULT_TEXT_FALLBACK.secondary_fallback_model;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5', mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {`Modelo primario (${retries} reintento${retries !== 1 ? 's' : ''})\n  -> ${fb1Provider}/${fb1Model}\n  -> ${fb2Provider}/${fb2Model}`}
      </Typography>
    </Paper>
  );
}

function AgentFormFallbackSettings({ isImageAgent, fallbackConfig, onFallbackChange }) {
  const config = fallbackConfig || {};

  const handleChange = (field, value) => {
    onFallbackChange({ ...config, [field]: value });
  };

  const handleNumberChange = (field, value) => {
    const parsed = parseInt(value, 10);
    onFallbackChange({ ...config, [field]: isNaN(parsed) ? 0 : parsed });
  };

  if (isImageAgent) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>Reintentos de Imagen</Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Max Reintentos"
              type="number"
              fullWidth
              size="small"
              value={config.image_max_retries ?? DEFAULT_IMAGE_FALLBACK.image_max_retries}
              onChange={(e) => handleNumberChange('image_max_retries', e.target.value)}
              helperText="Intentos con el modelo primario"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Esperar despues del intento #"
              type="number"
              fullWidth
              size="small"
              value={config.image_retry_delay_after ?? DEFAULT_IMAGE_FALLBACK.image_retry_delay_after}
              onChange={(e) => handleNumberChange('image_retry_delay_after', e.target.value)}
              helperText="Agregar espera despues de este intento"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Segundos de espera"
              type="number"
              fullWidth
              size="small"
              value={config.image_retry_delay_seconds ?? DEFAULT_IMAGE_FALLBACK.image_retry_delay_seconds}
              onChange={(e) => handleNumberChange('image_retry_delay_seconds', e.target.value)}
              helperText="Segundos entre reintentos tardios"
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={AI_IMAGE_PROVIDERS.map(p => p.name)}
              value={config.image_fallback_provider ?? DEFAULT_IMAGE_FALLBACK.image_fallback_provider}
              onChange={(e, v) => handleChange('image_fallback_provider', v || '')}
              onInputChange={(e, v, reason) => {
                if (reason === 'input') handleChange('image_fallback_provider', v);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Fallback Provider" size="small" helperText="Provider si todos los reintentos fallan" />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={
                AI_IMAGE_PROVIDERS.find(
                  p => p.name.toLowerCase() === (config.image_fallback_provider || DEFAULT_IMAGE_FALLBACK.image_fallback_provider).toLowerCase()
                )?.models || []
              }
              value={config.image_fallback_model ?? DEFAULT_IMAGE_FALLBACK.image_fallback_model}
              onChange={(e, v) => handleChange('image_fallback_model', v || '')}
              onInputChange={(e, v, reason) => {
                if (reason === 'input') handleChange('image_fallback_model', v);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Fallback Modelo" size="small" helperText="Modelo de fallback" />
              )}
            />
          </Grid>
        </Grid>
        <FallbackChainSummary isImageAgent={true} config={config} />
      </Box>
    );
  }

  // Text agent fallback
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Reintentos y Fallback de Texto</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Reintentos con modelo primario"
            type="number"
            size="small"
            value={config.max_retries ?? DEFAULT_TEXT_FALLBACK.max_retries}
            onChange={(e) => handleNumberChange('max_retries', e.target.value)}
            helperText="Veces que reintenta con el modelo configurado antes de saltar a fallback"
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            freeSolo
            options={AI_PROVIDERS.map(p => p.name)}
            value={config.primary_fallback_provider ?? DEFAULT_TEXT_FALLBACK.primary_fallback_provider}
            onChange={(e, v) => handleChange('primary_fallback_provider', v || '')}
            onInputChange={(e, v, reason) => {
              if (reason === 'input') handleChange('primary_fallback_provider', v);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Fallback 1 - Provider" size="small" helperText="Primer fallback" />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            freeSolo
            options={
              AI_PROVIDERS.find(
                p => p.name.toLowerCase() === (config.primary_fallback_provider || DEFAULT_TEXT_FALLBACK.primary_fallback_provider).toLowerCase()
              )?.models || []
            }
            value={config.primary_fallback_model ?? DEFAULT_TEXT_FALLBACK.primary_fallback_model}
            onChange={(e, v) => handleChange('primary_fallback_model', v || '')}
            onInputChange={(e, v, reason) => {
              if (reason === 'input') handleChange('primary_fallback_model', v);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Fallback 1 - Modelo" size="small" />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            freeSolo
            options={AI_PROVIDERS.map(p => p.name)}
            value={config.secondary_fallback_provider ?? DEFAULT_TEXT_FALLBACK.secondary_fallback_provider}
            onChange={(e, v) => handleChange('secondary_fallback_provider', v || '')}
            onInputChange={(e, v, reason) => {
              if (reason === 'input') handleChange('secondary_fallback_provider', v);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Fallback 2 - Provider" size="small" helperText="Ultimo recurso" />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            freeSolo
            options={
              AI_PROVIDERS.find(
                p => p.name.toLowerCase() === (config.secondary_fallback_provider || DEFAULT_TEXT_FALLBACK.secondary_fallback_provider).toLowerCase()
              )?.models || []
            }
            value={config.secondary_fallback_model ?? DEFAULT_TEXT_FALLBACK.secondary_fallback_model}
            onChange={(e, v) => handleChange('secondary_fallback_model', v || '')}
            onInputChange={(e, v, reason) => {
              if (reason === 'input') handleChange('secondary_fallback_model', v);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Fallback 2 - Modelo" size="small" />
            )}
          />
        </Grid>
      </Grid>
      <FallbackChainSummary isImageAgent={false} config={config} />
    </Box>
  );
}

export default AgentFormFallbackSettings;
